<template>
  <div class="page-header">
    <div class="row">
      <div class="col-12">
        <div class="page-header-left">
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb ml-0" itemscope itemtype="https://schema.org/BreadcrumbList">
              <li class="breadcrumb-item" itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
                <router-link :to="{ name: homeLink }" class="breadcrumb-home-link" itemprop="item" aria-label="Home">
                  <feather type="home" />
                  <meta itemprop="name" content="Home" />
                  <meta itemprop="position" content="1" />
                </router-link>
              </li>
              <li
                v-for="(item, index) in computedBreadcrumbs"
                :key="index"
                class="breadcrumb-item"
                :class="{ 'active': item.active }"
                itemprop="itemListElement"
                itemscope
                itemtype="https://schema.org/ListItem"
              >
                <router-link
                  v-if="!item.active"
                  :to="item.link"
                  itemprop="item"
                >
                  <span itemprop="name">{{ item.text }}</span>
                  <meta itemprop="position" :content="index + 2" />
                </router-link>
                <span v-else itemprop="name">{{ item.text }}</span>
                <meta v-if="item.active" itemprop="position" :content="index + 2" />
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Breadcrumbs component
 * 
 * A modern, accessible breadcrumb navigation component that supports both
 * simple title-based breadcrumbs and complex custom breadcrumb structures.
 * 
 * Features:
 * - Schema.org markup for SEO
 * - ARIA attributes for accessibility
 * - Responsive design
 * - Dynamic link interpolation
 * - Customizable home link
 */
export default {
  props: {
    /**
     * The title of the current page (used as the last breadcrumb item)
     */
    title: {
      type: String,
      default: 'home'
    },
    /**
     * The text for the main section breadcrumb (parent of the current page)
     */
    main: {
      type: String,
      default: ''
    },
    /**
     * The route name for the main section breadcrumb
     */
    mainLink: {
      type: String,
      default: 'welcome'
    },
    /**
     * Custom breadcrumbs array. If provided, title and main props are ignored.
     * Each item should have: { text, link, active? }
     */
    breadcrumbs: {
      type: Array,
      default: () => []
    },
    /**
     * The route name for the home breadcrumb
     */
    homeLink: {
      type: String,
      default: 'welcome'
    }
  },
  computed: {
    /**
     * Computes the final breadcrumb items to display
     * @returns {Array} Array of breadcrumb items with text, link, and active properties
     */
    computedBreadcrumbs() {
      if (this.breadcrumbs.length > 0) {
        return this.breadcrumbs.map((item, index) => ({
          text: item.text,
          link: this.interpolate(item.link),
          active: item.active || index === this.breadcrumbs.length - 1
        }));
      } else {
        const crumbs = [];
        if (this.main) {
          crumbs.push({text: this.main, link: {name: this.mainLink}, active: false});
        }
        if (this.title) {
          crumbs.push({text: this.title, link: '', active: true});
        }
        return crumbs;
      }
    }
  },
  methods: {
    /**
     * Interpolates dynamic values in link strings
     * @param {String|Object} link - The link to interpolate
     * @returns {String|Object} The interpolated link
     */
    interpolate(link) {
      if (typeof link === 'string' && link.includes('${')) {
        return link.replace(/\$\{([^\}]+)\}/g, (_, key) => {
          const keys = key.split('.');
          let value = this;
          keys.forEach(k => {
            value = value[k];
          });
          return value;
        });
      }
      // Check if link is a direct path
      return typeof link === 'string' && link.startsWith('/') ? { path: link } : { name: link };
    }
  }
}
</script>

<style scoped>
.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  list-style: none;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
}

/* Add a separator between breadcrumb items */
.breadcrumb-item + .breadcrumb-item::before {
  content: "/";
  display: inline-block;
  padding: 0 0.5rem;
  color: #6c757d;
}

/* Style for the home icon */
.breadcrumb-home-link {
  display: flex;
  align-items: center;
  color: #007bff;
  transition: color 0.2s ease-in-out;
}

.breadcrumb-home-link:hover {
  color: #0056b3;
}

/* Style for breadcrumb links */
.breadcrumb-item a {
  color: #007bff;
  text-decoration: none;
  transition: color 0.2s ease-in-out;
}

.breadcrumb-item a:hover {
  color: #0056b3;
  text-decoration: underline;
}

/* Style for active breadcrumb item */
.breadcrumb-item.active {
  color: #6c757d;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .breadcrumb {
    padding: 0.5rem;
    font-size: 0.875rem;
  }
}
</style>
